import { useCallback, useEffect, useState } from "react";
import { questionsOf } from "../../shared/questions";
import type { ApprovalDTO, ReviewPageDTO } from "../../shared/types";
import { useUser } from "../App";
import { AgentAvatar } from "../components/AgentAvatar";
import { ErrorText, PageHeader } from "../components/ui";
import { List } from "../components/List";
import { Markdown } from "../components/Markdown";
import { HandoverCard } from "../components/HandoverCard";
import { ActionCard } from "../components/ActionCard";
import { ProposalDetails } from "../components/ProposalCard";
import { NoteCard } from "../components/NoteCard";
import { RequestCard } from "../components/RequestCard";
import { Overlay } from "../components/Overlay";
import { ReviewCard } from "../components/ReviewCard";
import { LockedVerdicts, VerdictBar } from "../components/Verdicts";
import { Tabs, useTabParam } from "../components/Tabs";
import { TextLink } from "../components/TextLink";
import { RequestTemplates } from "./Review";
import { ApprovalRow, byUrgency, Heading, isProposal, Meta } from "./RequestList";
import { get } from "../lib/api";
import { canDecideRequest, formatApprovalText } from "../lib/approval-text";

/** Which requests the list asks for. Members always get their own, whichever they ask for; an admin starts on theirs. */
const VIEWS = [["mine", "Mine"], ["pending", "All pending"], ["all", "All"]] as const;

/** The agent's text as a document, and for a question the card that answers it. The viewer and the page share it. */
function RequestBody({ a, admin, onAnswered }: { a: ApprovalDTO; admin: boolean; onAnswered: (updated: ApprovalDTO) => void }) {
  const user = useUser();
  const questions = questionsOf(a);
  // A single free-text ask has the question as its title, so the card alone says it.
  const titled = a.kind !== "question" || questions.length > 1 || questions[0]?.question !== a.title;
  return (
    <>
      {titled && <h1 className="text-title font-semibold tracking-tight break-words">{a.title}</h1>}
      <div className={`text-xs text-muted ${titled ? "mt-2" : ""}`}>
        <Meta a={a} />
        {admin && (
          <>
            {" · "}
            <TextLink to={`/admin/agents/${a.agentId}`} quiet>
              open agent
            </TextLink>
          </>
        )}
        {a.conversationId && (
          <>
            {" · "}
            <TextLink to={admin ? `/admin/conversations/${a.conversationId}` : `/c/${a.conversationId}`} quiet>
              open the chat
            </TextLink>
          </>
        )}
      </div>
      {a.error && <p className="mt-3 text-sm text-danger">Could not apply: {a.error}</p>}
      {/* An action's reason is on its card, as plain text: nothing an agent wrote beside a call may load anything. */}
      {a.reason && a.kind !== "action" && <Markdown className="mt-6 text-reading leading-7 [&_h4]:mt-7 [&_h4]:text-xs [&_h4]:tracking-wider [&_h4]:text-muted">{formatApprovalText(a.reason)}</Markdown>}
      <ProposalDetails a={a} canDecide={canDecideRequest(a, user)} />
      {a.kind === "question" ? (
        <div className="mt-6">
          <RequestCard approval={a} canAnswer={canDecideRequest(a, user)} canTypeHere onAnswered={onAnswered} />
        </div>
      ) : a.kind === "note" ? (
        <div className="mt-6">
          <NoteCard approval={a} canDecide={canDecideRequest(a, user)} />
        </div>
      ) : a.kind === "review" ? (
        <div className="mt-6">
          <ReviewCard approval={a} canDecide={canDecideRequest(a, user)} onDecided={onAnswered} />
        </div>
      ) : a.kind === "schedule_handover" ? (
        <div className="mt-6">
          <HandoverCard approval={a} canDecide={canDecideRequest(a, user)} onDecided={onAnswered} />
        </div>
      ) : a.kind === "action" ? (
        <div className="mt-6">
          <ActionCard approval={a} canDecide={canDecideRequest(a, user)} onDecided={onAnswered} />
        </div>
      ) : null}
    </>
  );
}

/** A pending proposal's three verdicts for whoever decides it, and the same drawn off, with who decides, for anyone else. */
function ProposalVerdicts({ a, onDecided }: { a: ApprovalDTO; onDecided: () => void }) {
  const user = useUser();
  if (a.status !== "pending" || !isProposal(a)) return null;
  return canDecideRequest(a, user) ? <VerdictBar a={a} onDecided={onDecided} /> : <LockedVerdicts a={a} />;
}

/** Full-screen reader for one request: the agent's text formatted as a document, a proposal's verdicts pinned to the bottom. */
function ApprovalViewer({ a, admin, onClose, onChanged }: { a: ApprovalDTO; admin: boolean; onClose: () => void; onChanged: () => void }) {
  return (
    <Overlay
      onClose={onClose}
      leading={<AgentAvatar agent={a.agentId} name={a.agentName} />}
      title={<Heading a={a} />}
      footer={
        // A question, a note and a templated item are decided on their own card; only a proposal gets the pinned bar.
        a.status === "pending" && isProposal(a) ? (
          <div className="mx-auto max-w-3xl">
            <ProposalVerdicts
              a={a}
              onDecided={() => {
                onClose();
                onChanged();
              }}
            />
          </div>
        ) : undefined
      }
    >
      <article className="mx-auto max-w-3xl px-4 py-6 md:px-6 md:py-10">
        <RequestBody a={a} admin={admin} onAnswered={onChanged} />
      </article>
    </Overlay>
  );
}

/** One request at its own address, so a link can go straight to it. */
export function RequestPage({ approvalId, admin = false }: { approvalId: string; admin?: boolean }) {
  const [a, setA] = useState<ApprovalDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => {
    get<ApprovalDTO>(`/api/approvals/${approvalId}`).then(setA, (e) => setError(e.message));
  }, [approvalId]);
  useEffect(load, [load]);

  return (
    <div className="h-full overflow-y-auto pb-10">
      <article className="mx-auto max-w-3xl px-4 py-6 md:px-6 md:py-10">
        <TextLink to={admin ? "/admin/approvals" : "/approvals"} quiet className="text-sm text-muted">
          ← Requests
        </TextLink>
        {error && (
          <div className="mt-4">
            <ErrorText error={error} />
          </div>
        )}
        {!a ? (
          !error && <div className="card mt-4 h-40 animate-pulse" />
        ) : (
          <>
            <div className="mt-5 flex items-center gap-3">
              <AgentAvatar agent={a.agentId} name={a.agentName} />
              <Heading a={a} />
            </div>
            <div className="mt-6">
              <RequestBody a={a} admin={admin} onAnswered={setA} />
            </div>
            {a.status === "pending" && isProposal(a) && (
              <div className="mt-8">
                <ProposalVerdicts a={a} onDecided={load} />
              </div>
            )}
          </>
        )}
      </article>
    </div>
  );
}

/**
 * Requests: everything an agent wants a person to decide (D14) — its questions, what it wants to start on its own,
 * and what it made that needs a verdict before it is used. Two tabs (Dimi, 2026-10-19): All requests, the list, and
 * Request templates, one row per template opening its own page. Everyone starts on what is addressed to them; an admin
 * may also read everyone's, and decides only their own.
 */
export function ApprovalsPage({ admin = false }: { admin?: boolean }) {
  const [items, setItems] = useState<ApprovalDTO[] | null>(null);
  const [pages, setPages] = useState<ReviewPageDTO[] | null>(null);
  const [tab, setTab] = useTabParam(["requests", "templates"] as const);
  const [view, setView] = useTabParam(["mine", "pending", "all"] as const, "view");
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const opened = items?.find((a) => a.id === openId) ?? null;
  const close = useCallback(() => setOpenId(null), []);

  const load = () => {
    get<ApprovalDTO[]>(`/api/approvals?view=${view}`).then((list) => setItems(byUrgency(list)), (e) => setError(e.message));
    get<ReviewPageDTO[]>("/api/review/pages").then(setPages, (e) => setError(e.message));
  };
  const waiting = pages?.reduce((n, p) => n + p.waiting, 0) ?? 0;
  useEffect(() => {
    load();
    const timer = setInterval(load, 15_000);
    return () => clearInterval(timer);
  }, [view]);

  return (
    <div className="h-full overflow-y-auto pb-10">
      <PageHeader
        title="Requests"
        subtitle={
          admin
            ? "What agents are waiting on: questions, what they want to start on their own, and what they made that needs a verdict before it is used. Each is addressed to one person, or to the agent's owners, and only they decide it."
            : "What agents are waiting on you for: a question to answer, or something they made that needs your verdict before it is used. Decide it here or in the chat it came from, and the agent carries on."
        }
      />
      <div className="max-w-4xl space-y-4 px-4 md:px-6">
        <Tabs
          label="Requests"
          value={tab}
          onChange={setTab}
          tabs={[
            { key: "requests", label: "All requests", icon: "audit" },
            { key: "templates", label: "Request templates", icon: "pages", count: waiting || undefined },
          ]}
        />
        <ErrorText error={error} />
        {tab === "templates" ? (
          <RequestTemplates pages={pages} admin={admin} />
        ) : (
          <>
            {admin && <Tabs size="sm" label="Show" value={view} onChange={setView} tabs={VIEWS.map(([key, label]) => ({ key, label }))} />}
            {!items ? (
              <div className="card h-32 animate-pulse" />
            ) : (
              <List
                empty={
                  <div className="flex flex-col items-center gap-1 py-4">
                    <img src="/favicon.png" alt="Pandito" className="mb-1 size-12 rounded-2xl shadow-sm ring-1 ring-line" />
                    <span className="text-sm font-medium text-fg">Nothing waiting</span>
                    <span className="text-xs text-muted">When an agent needs something from you, it shows up here and in the chat it came from.</span>
                  </div>
                }
              >
                {items.map((a) => (
                  <ApprovalRow key={a.id} a={a} onOpen={() => setOpenId(a.id)} />
                ))}
              </List>
            )}
          </>
        )}
        {opened && <ApprovalViewer a={opened} admin={admin} onClose={close} onChanged={load} />}
      </div>
    </div>
  );
}
